import React from 'react';
import type { TimeGranularity } from '../../../utils/timeGranularity';
import { GRANULARITY_LABELS } from '../../../utils/timeGranularity';

interface Props {
  hedgesDerivedData: any[]; // enriched chartData items (single source of truth)
  selectedMonthIndex?: number;
  timeGranularity?: TimeGranularity;
  derivedMaterials: Array<{
    formula_code: string;
    formula_name: string;
    price_ratio: number;
  }>;
  formulaColorDictionary: Record<string, string>;
}

export const HedgesDerivedMonthDetail: React.FC<Props> = ({
  hedgesDerivedData,
  selectedMonthIndex = 0,
  derivedMaterials,
  formulaColorDictionary,
  timeGranularity = 'Month',
}) => {
  const isAggregated = timeGranularity !== 'Month';
  // +1 to skip on-hand bar at index 0
  const dataIndex = selectedMonthIndex + 1;
  const d = hedgesDerivedData?.[dataIndex];

  if (!d) {
    return (
      <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4">
        <div className="text-gray-400 text-sm text-center py-8">No data available</div>
      </div>
    );
  }

  const calculationDetails = d.calculationDetails || {};
  const percentages = d.percentages || {};

  // Total exposure value across all materials
  const totalAbsValue = Object.values(calculationDetails).reduce((sum: number, det: any) => sum + Math.abs(det.monetaryScore), 0);

  // Build allocation data for each material
  const allocations = derivedMaterials.map(dm => {
    const details = calculationDetails[dm.formula_code];
    const pct = percentages[dm.formula_code] || 0;
    const color = formulaColorDictionary[dm.formula_code] || '#6B7280';
    return { ...dm, details, pct, color };
  }).filter(a => a.details);

  return (
    <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4 max-h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span style={{ color: 'rgba(59, 130, 246, 0.5)', fontSize: '0.65em' }}>&#9654;</span>
          <span className="text-base font-semibold text-gray-900">{d.month}</span>
          <span className="text-sm text-gray-400">{isAggregated ? `${GRANULARITY_LABELS[timeGranularity]} ` : ''}Hedge Allocation</span>
        </div>
      </div>
      {isAggregated && (
        <div className="text-xs text-gray-400 mb-3 leading-tight">
          Supply and demand are summed across all months in this {GRANULARITY_LABELS[timeGranularity].toLowerCase()} period. Allocations are recalculated from aggregated exposures.
        </div>
      )}

      {/* Allocation bar — proportional split across materials */}
      <div className="mb-2">
        <div className="text-xs font-medium text-gray-500 mb-1.5">Allocation split</div>
        <div className="relative h-5 rounded-md bg-gray-100 overflow-hidden flex">
          {allocations.map((a) => (
            <div
              key={a.formula_code}
              className="h-full"
              style={{ width: `${a.pct * 100}%`, backgroundColor: a.color }}
            />
          ))}
        </div>
      </div>

      {/* Allocation legend — compact row under the bar */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4 text-xs text-gray-600">
        {allocations.map((a) => (
          <div key={a.formula_code} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: a.color }} />
            <span>{a.formula_name}</span>
            <span className="font-bold text-gray-900">{(a.pct * 100).toFixed(0)}%</span>
          </div>
        ))}
      </div>

      {/* Verdict — total exposure value */}
      <div className="p-2.5 rounded-lg text-sm mb-4 bg-blue-50">
        <div className="flex justify-between items-center">
          <span className="text-blue-700 font-medium">Total exposure value</span>
          <span className="text-blue-700 font-bold tabular-nums">
            ${totalAbsValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        </div>
      </div>

      {/* Per-material detail cards */}
      <div className="space-y-2.5">
        {allocations.map((a) => {
          const det = a.details!;
          const isPositive = det.finalExposure >= 0;

          return (
            <div key={a.formula_code} className="border border-gray-150 rounded-lg p-3">
              {/* Material header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: a.color }} />
                  <span className="text-sm font-semibold text-gray-900">{a.formula_name}</span>
                </div>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{
                  backgroundColor: `${a.color}15`,
                  color: a.color,
                }}>
                  {(a.pct * 100).toFixed(0)}%
                </span>
              </div>

              {/* Step 1: Quantity exposure = Demand + Buffer - Supply */}
              <div className="space-y-0.5 text-xs mb-1.5">
                <div className="flex justify-between">
                  <span className="text-gray-500">Demand (MRP)</span>
                  <span className="text-red-600 tabular-nums">{det.demand.toFixed(0)} MT</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">+ Buffer</span>
                  <span className="text-amber-600 tabular-nums">+{det.buffer.toFixed(0)} MT</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">− Supply (Storage + POs)</span>
                  <span className="text-green-600 tabular-nums">−{det.supply.toFixed(0)} MT</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-gray-100">
                  <span className="font-semibold text-gray-700">Exposure</span>
                  {(() => {
                    const raw = det.demand + det.buffer - det.supply;
                    const clamped = raw < 0;
                    return (
                      <span className={`font-semibold tabular-nums ${clamped ? 'text-gray-400' : 'text-red-700'}`}>
                        {clamped ? `${raw.toFixed(0)} → 0 MT` : `${det.finalExposure.toFixed(0)} MT`}
                      </span>
                    );
                  })()}
                </div>
              </div>

              {/* Storage flow */}
              <div className="text-xs bg-blue-50 rounded px-2 py-1.5 space-y-0.5 mb-1.5">
                <div className="flex justify-between text-gray-500">
                  <span>Storage start</span>
                  <span className="tabular-nums">{det.startStorage.toFixed(0)} MT</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Storage end →</span>
                  <span className="tabular-nums">{det.endStorage.toFixed(0)} MT</span>
                </div>
              </div>

              {/* Step 2: Monetary conversion → allocation basis */}
              <div className="text-xs bg-gray-50 rounded px-2 py-1.5 space-y-0.5">
                <div className="flex justify-between text-gray-500">
                  <span>× ${Number(det.spotPrice).toLocaleString(undefined, { maximumFractionDigits: 0 })} × {Number(det.ratio).toFixed(2)}</span>
                  <span className={`font-bold tabular-nums ${isPositive ? 'text-green-700' : 'text-red-700'}`}>
                    ${Math.abs(det.monetaryScore).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">→ allocation</span>
                  <span className="font-bold tabular-nums" style={{ color: a.color }}>
                    {(a.pct * 100).toFixed(0)}% of hedge
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
